import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { useRecipientTeams, useSaveRecipientTeams } from "@/hooks/use-app-settings";

const emailCsv = z.string().refine((val) => {
  if (!val.trim()) return true;
  const emails = val
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return emails.every((e) => z.string().email().safeParse(e).success);
}, "One or more invalid email addresses");

const schema = z.object({
  postDelivery: emailCsv,
  deliveryCompliance: emailCsv,
});

type FormValues = z.infer<typeof schema>;

export function RecipientTeamsSection() {
  const { data, isLoading, isError, refetch } = useRecipientTeams();
  const save = useSaveRecipientTeams();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { postDelivery: "", deliveryCompliance: "" },
  });

  useEffect(() => {
    if (data) {
      form.reset({ postDelivery: data.postDelivery, deliveryCompliance: data.deliveryCompliance });
    }
  }, [data, form]);

  if (isError) {
    return <ErrorState message="Failed to load recipient teams" onRetry={() => refetch()} />;
  }

  async function onSubmit(values: FormValues) {
    await save.mutateAsync({
      postDelivery: values.postDelivery.trim(),
      deliveryCompliance: values.deliveryCompliance.trim(),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recipient Teams</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-6">
          Configure the email addresses that receive notifications for each delivery team. Enter
          comma-separated email addresses.
        </p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="postDelivery"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Post Delivery</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="alice@example.com, bob@example.com"
                      className="resize-none"
                      rows={3}
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>Recipients notified for post-delivery events.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="deliveryCompliance"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Delivery Compliance</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="compliance@example.com, auditor@example.com"
                      className="resize-none"
                      rows={3}
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Recipients notified for delivery compliance events.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={save.isPending || isLoading}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
